import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { FormSection } from '../../common/components/SectionedForm'
import { api, buildPaginatedUrl } from '../../common/api'
import { useTranslation } from 'react-i18next'
import { PhoneBookEntry, ServerPage } from '../../app/types'
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, ListItemIcon, ListItemText, Menu } from '@mui/material'
import { Sort, useInfiniteScroll } from '../../common/components/table/useInfiniteScroll'
import { useUrlHelpers, useUserWriteAccess } from '../../common/util/hooks'
import { ColumnDefinition, InfiniteScrollTable } from '../../common/components/table/Table'
import TableRowActionsMenu from '../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../common/components/table/SorterHeader'
import { AddButton } from '../../common/components/AddButton'
import CancelButton from '../../common/form/CancelButton'
import ErrorAlert from '../../common/components/ErrorAlert'
import DeletionModal from '../../common/components/DeletionModal'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import IconButton from '@mui/material/IconButton'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import FileDropZone from '../../common/components/upload/FileDropZone'
import usePhoneBookForm from '../../phonebook/usePhoneBookForm'
import { useOktaAuth } from '@okta/okta-react'
import { MAX_CSV_FILE_SIZE } from '../../phonebook'

export const usePhonebookSection = (userId: string, targetEmail?: string): FormSection<PhoneBookEntry[]> => {
    const { t } = useTranslation()
    const {createUrl} = useUrlHelpers();
    const [entryToDelete, setEntryToDelete] = useState<PhoneBookEntry | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<PhoneBookEntry | null>(null)
    const hasWriteAccess = useUserWriteAccess(targetEmail)
    const onlyWithWriteAccess = useCallback((element: ReactElement) => hasWriteAccess ? element : <></>, [hasWriteAccess])
    const [openImportCsvDialog, setOpenImportCsvDialog] = useState<boolean>(false)
    const [openClearConfirmation, setOpenClearConfirmation] = useState<boolean>(false)
    const [clearExistingValuesOnUpload, setClearExistingValuesOnUpload] = useState<boolean>(false)

    const fetchEntries = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<PhoneBookEntry>> => {
            const { data } = await api.get(buildPaginatedUrl(createUrl(`user/${userId}/phonebook`), pageParam, sortParam, searchTermParam, 10000))
            return data
        },
        [createUrl, userId]
    )

    const { sort, setSort, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<PhoneBookEntry>({
        queryKey: `phonebook-user-${userId}`,
        initialSort: { field: 'name', order: 'asc' },
        fetchFunction: fetchEntries,
    })

    const onSubmit = useCallback(
        async (formData: PhoneBookEntry) => {
            const url = createUrl(`user/${userId}/phonebook`)
            if (entryToUpdate?.uuid) {
                await api.put(url + `/${entryToUpdate.name}`, formData)
            } else {
                await api.post(url, formData)
            }

            setEntryToUpdate(null)

            await refetch()
        },
        [refetch, setEntryToUpdate, entryToUpdate, createUrl, userId]
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = usePhoneBookForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: PhoneBookEntry) => {
                    reset()
                    setEntryToUpdate({...record, uuid: 'dummy'})
                    setValue('name', record.name)
                    setValue('number', record.number)
                    return false
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: PhoneBookEntry) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, setValue, reset]
    )

    const columns: ColumnDefinition<PhoneBookEntry>[] = useMemo(
        () => [
            {
                title: t('phonebook.table.name'),
                key: 'name',
                sorter: false,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('phonebook.table.number'),
                key: 'number',
                sorter: false,
                render: (text: string) => <>{text}</>,
            },
            {
                title: '',
                key: 'uuid',
                sorter: false,
                render: (text: string, record: PhoneBookEntry) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess}/>,
            },
        ],
        [t, rowActions, hasWriteAccess]
    )

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(entry => entry.uuid, [])

    const getDeleteUrl = useCallback(i => i ? createUrl(`user/${userId}/phonebook/${i.name}`) : null, [createUrl, userId])

    const oktaAuth = useOktaAuth();

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const buttonPanel = useMemo(
        () => (
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                {onlyWithWriteAccess(<Grid item>
                        <AddButton
                            onClick={() => {
                                reset()
                                setEntryToUpdate({ name: '', number: '', uuid: '' })
                                return null
                            }}
                            label={`${t('phonebook.create-new')}`}
                        />
                    </Grid>)
                }
                <Grid item xs={'auto'} pr={1}>
                    <IconButton color="secondary" onClick={handleClick}>
                        <SettingsIcon/>
                    </IconButton>
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        {<MenuItem disabled={!hasWriteAccess} onClick={() => setOpenImportCsvDialog(true)}>
                            <ListItemIcon>
                                <FileDownloadIcon/>
                            </ListItemIcon>
                            <ListItemText>{t("phonebook.import-csv-title")}</ListItemText>
                        </MenuItem>}
                        <MenuItem onClick={async () => {
                            const url = createUrl( `user/${userId}/phonebook/download`)
                            fetch(url, {
                                method: 'GET',
                                headers: {
                                    ...(oktaAuth?.authState?.isAuthenticated? {"authorization" : `Bearer ${oktaAuth?.authState?.accessToken?.accessToken}`} : {})
                                },
                            })
                                .then(response => response.blob())
                                .then(blob => {
                                    // Create blob link to download
                                    const url = window.URL.createObjectURL(new Blob([blob]))
                                    const link = document.createElement('a')
                                    link.href = url
                                    link.setAttribute('download', `pb_${userId}.csv`)

                                    document.body.appendChild(link)
                                    link.click()
                                    link.parentNode?.removeChild(link)
                                })
                        }}>
                            <ListItemIcon>
                                <FileUploadIcon/>
                            </ListItemIcon>
                            <ListItemText>{t("phonebook.export-csv-title")}</ListItemText>
                        </MenuItem>
                        {<>  <Divider/>
                                <MenuItem disabled={!hasWriteAccess} onClick={() => setOpenClearConfirmation(true)}>
                                    <ListItemIcon>
                                        <ClearIcon/>
                                    </ListItemIcon>
                                    <ListItemText>{t("phonebook.clear")}</ListItemText>
                                </MenuItem>
                            </>}
                    </Menu>
                </Grid>
            </Grid>
        ),
        [t, reset, createUrl, userId,
            oktaAuth?.authState?.accessToken?.accessToken, oktaAuth?.authState?.isAuthenticated,
            anchorEl, handleClose, hasWriteAccess, onlyWithWriteAccess
        ]
    )

    const onCsvFileUploaded = useCallback(file => {
        refetch();
        handleClose();
        setOpenImportCsvDialog(false);

    }, [refetch, handleClose])

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.uuid ? 'phonebook.update-dialog-title' : 'phonebook.create-new')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t]
    )

    const importCSVForm = useMemo(
        () => (
            <Dialog open={openImportCsvDialog} onClose={() => setOpenImportCsvDialog(false)}>
                <DialogTitle>
                    {t('phonebook.import-csv-title')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenImportCsvDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme => theme.palette.grey[500],
                        }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {
                        <Grid container>
                            <Grid item>
                                <FormControlLabel
                                    sx={{ padding: 2 }}
                                    control={
                                        <Checkbox
                                            disabled={false}
                                            checked={clearExistingValuesOnUpload}
                                            onChange={event => {
                                                setClearExistingValuesOnUpload(event.target.checked)
                                            }}
                                        />
                                    }
                                    key={'clearExisting'}
                                    label={t('phonebook.clear-existing-entries')}
                                />
                            </Grid>
                            <Grid item>
                                <FileDropZone
                                    uploadUrl={createUrl(`user/${userId}/phonebook/csv?clearExisting=${clearExistingValuesOnUpload}`)}
                                    onFileUploaded={onCsvFileUploaded}
                                    acceptMime=".csv"
                                    maxSizeInBytes={MAX_CSV_FILE_SIZE}
                                    dropzoneTitle={t('phonebook.import-csv-dropzone-title')}
                                    showSuccess={true}
                                />
                            </Grid>
                        </Grid>
                    }
                </DialogContent>
                <DialogActions>
                    <CancelButton title={t('general.close')} onClick={() => setOpenImportCsvDialog(false)} />
                </DialogActions>
            </Dialog>
        ),
        [t, onCsvFileUploaded, openImportCsvDialog, clearExistingValuesOnUpload, createUrl, userId]
    )

    const clearPhonebook = useCallback(async () => {
        await api.put(createUrl(`user/${userId}/phonebook/clear`))
        await refetch()
        setOpenClearConfirmation(false)
        handleClose()
    }, [refetch, createUrl, userId, handleClose])

    const renderDetails = useCallback(
        (data: PhoneBookEntry[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <ErrorAlert showAlert={!!error} />
                    <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={1} flexWrap={'nowrap'} pb={3}>
                        <Grid item xs={'auto'} pr={1}>
                            <></>
                        </Grid>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <InfiniteScrollTable<PhoneBookEntry>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={isFetching}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'userPhonebookTable'}
                        />
                    </Grid>

                    <DeletionModal<PhoneBookEntry | null>
                        nameToDelete={(entryToDelete: any) => entryToDelete?.name || ''}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />

                    <DeletionModal<string | null>
                        title={t('phonebook.clear')}
                        text={() => t('phonebook.clear-confirmation')}
                        itemToDelete={openClearConfirmation ? 'dummy' : null}
                        deleteAction={clearPhonebook}
                        onClose={() => setOpenClearConfirmation(false)}
                        onSuccess={refetch}
                    />

                    {entryForm}
                    {importCSVForm}
                </Grid>
            )
        },
        [ columns, buttonPanel, entryForm, entryToDelete, error, clearPhonebook, isFetching, t,
            openClearConfirmation, importCSVForm,
            fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, refetch, rows, sorter]
    )

    return {
        title: t("navigation.phonebook"),
        data: rows,
        isFetching: isFetching || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <MenuBookIcon/>
    }
}